import PromoStripLeft from "@/assets/signup/promostripusleft";
import PromoStripRight from "@/assets/signup/promostripusright";
import useCountdown from "@/hooks/useCountdown";
import { useI18nContext } from "@/hooks/useI18nContext";
import Amplitude from "@/shared/services/amplitude.service";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
} from "@/shared/utils/commons";
import { getFromLocal } from "@/shared/utils/storage.util";
import {
  getUTMFieldsFromLocalStorage,
  stringifyUTMParams,
} from "@/shared/utils/utm.util";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";

interface PromotionStripProps {
  data: {
    type: string;
    preText ?: string;
    percentage: number;
    endAfterHour: number;
    postText ?: string;
    href: string;
    amplitudeEvent: {
      eventName: string;
      eventProperties: { [key: string]: string };
    };
  };
  source?: "navbar";
}

export const PromotionStrip: React.FC<PromotionStripProps> = ({
  data,
  source,
}) => {
  const { percentage, endAfterHour, href, amplitudeEvent, preText, postText } = data;
  const path = usePathname();
  const { hours, minutes, seconds } = useCountdown(path, endAfterHour);
  const [showStrip, setShowStrip] = useState(true);

  const params = useSearchParams();
  const gclid = params.get("gclid");
  const params_q = params.get("q");
  const isUsLocale = useI18nContext().locale === "us";
  const router = useRouter();
  const url = router.query.page;
  const [days, setDays] = useState(0);
  const [blackFridayHours, setBlackFridayHours] = useState(0);
  const [blackFridayMinutes, setBlackFridayMinutes] = useState(0);
  const [blackFridaySeconds, setBlackFridaySeconds] = useState(0);
  const [isBeforeBlackFridayEnd, setIsBeforeBlackFridayEnd] = useState(false);
  
  useEffect(() => {
    const blackFridayEnd = new Date('2025-11-28T23:59:59').getTime();
    const now = new Date().getTime();
    const distance = blackFridayEnd - now;
    const isBeforeBlackFridayEnd = now < blackFridayEnd;
    setDays(Math.floor(distance / (1000 * 60 * 60 * 24)));
    setBlackFridayHours(Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    setBlackFridayMinutes(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)));
    setBlackFridaySeconds(Math.floor((distance % (1000 * 60)) / 1000));
    setIsBeforeBlackFridayEnd(isBeforeBlackFridayEnd);
    const id = setInterval(() => {
      const now = new Date().getTime();
      const distance = blackFridayEnd - now;
      setDays(Math.floor(distance / (1000 * 60 * 60 * 24)));
      setBlackFridayHours(Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
      setBlackFridayMinutes(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)));
      setBlackFridaySeconds(Math.floor((distance % (1000 * 60)) / 1000));
      setIsBeforeBlackFridayEnd(now < blackFridayEnd);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const isSpecialPage = isUsLocale && (
    path.includes('kajabi-alternative') ||
    path.includes('teachable-alternative') ||
    path.includes('thinkific-alternative') ||
    path.includes('online-courses')
  );

  useEffect(() => {  
    window.addEventListener("scroll", () => {
      if (scrollY < 1) {
        setShowStrip(true);
      } else {
        setShowStrip(false);
      }
    });
    return () => {
      setShowStrip(true);
    };
  }, []);

  if (!showStrip && source !== "navbar" && !isMobile) return null;

  const handleClick = () => {
    if (path.includes("online-course-platform")) {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;

      if (scrollY === 0) {
        window.scrollTo({ top: viewportHeight * 0.01, behavior: 'smooth' });
      } else if (scrollY > viewportHeight * 0.01) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    if (href === null || href === undefined) return;

    let utmFields = getUTMFieldsFromLocalStorage();
    utmFields["utm_content"] = gclid ? gclid : "autobookdemos";
    const isUrlParams = new URLSearchParams(data.href).size;

    const utmParams = stringifyUTMParams(utmFields);

    let url = `${data.href}${isUrlParams ? "&" : ""}${utmParams}`;
    window.open(url, "_blank");

    new Amplitude().sendEvent({
      type: amplitudeEvent?.eventName,
      properties: {
        ...amplitudeEvent.eventProperties,
        current_url: getCurrentURL(),
        utm_params: getFromLocal("utmFields"),
        screen_width: getScreenWidth(),
        referrer: getReferrer(),
      } as { [key: string]: string | number },
    });
  };
  return (
    <div
  className={`${
    source === "navbar" ? "rounded-full" : ""
  } sticky top-[64px] flex cursor-pointer flex-col items-center justify-center gap-2 ${isSpecialPage && isBeforeBlackFridayEnd ? "bg-[#3A3D50]" : "bg-[#FFF8D4]"} p-3 md:p-2 text-center sm:flex-row sm:gap-3 md:top-[88px] relative ${showStrip && isBeforeBlackFridayEnd ? 'md:py-3' : 'md:py-1.5'}`}
  onClick={handleClick}
>
      {isSpecialPage && isBeforeBlackFridayEnd ? (
        <>
          {/* Left Side BLACK FRIDAY */}
          <div className={`absolute left-0 lg:h-full lg:ml-16 md:h-[40px] md:ml-8 ${showStrip ? 'hidden md:flex items-center' : 'hidden'}`}
            style={{ height: showStrip ? "-webkit-fill-available" : "40px" }}>
            <PromoStripLeft />
          </div>

          {/* Center Content */}
          <div className="flex-1 flex flex-col md:flex-col lg:flex-row justify-center items-center">
            <p className="text-white text-sm sm:text-title-16 font-semibold text-center sm:text-left md:text-sm md:mb-2 lg:mb-0 md:p-2">
              Black Friday Early Access: Unlock All Features at <span className="text-white">25% off</span>
            </p>
            <p className="rounded-full bg-[#10A750] px-2 py-1 text-xs sm:text-title-12 font-semibold text-white whitespace-nowrap">
              Ends in {days}d {blackFridayHours}h {blackFridayMinutes}m {blackFridaySeconds}s
            </p>
          </div>

          {/* Right Side Discount Cloud */}
          <div className={`absolute right-0 lg:h-full lg:mr-10 md:h-[40px] md:mr-8 ${showStrip ? 'hidden md:flex items-center' : 'hidden'}`}
            style={{ height: showStrip ? "-webkit-fill-available" : "40px" }}>
            <PromoStripRight />
          </div>
        </>
      ) : (
  <>
      <p className="text-title-16 font-semibold">
         {preText ? preText : "Get"}  <span className="text-green-2"> {percentage}% off</span>  {postText ? postText : "on booking a demo today"}
      </p>
      <p className="rounded-full bg-green-3 px-3 py-1.5 text-title-12 font-semibold text-white">
        Offer ends in {hours}h {minutes}m {seconds}s
      </p>
  </>
)}
  </div>
  );
};